'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useState, useMemo, type FormEvent } from 'react'
import { createClient } from '@/lib/supabase/client'
import MovieGrid from '@/components/movie-grid'
import MovieDetail from '@/components/movie-detail'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorBoundary } from '@/components/error-boundary'
import { Movie, Showtime, Theatre, FilterOptions, UserLocation } from '@/types'
import { Loader2, X, SlidersHorizontal, Search } from 'lucide-react'
import { getLocation, formatDateForAPI, StoredLocation } from '@/lib/location'
import { parseShowtimeAttributes, getFormatPriority } from '@/lib/amc-api'
import LocationPicker from '@/components/location-picker'

export default function HomePage() {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [movies, setMovies] = useState<Movie[]>([])
  const [allMovies, setAllMovies] = useState<Movie[]>([])
  const [theaters, setTheaters] = useState<Theatre[]>([])
  const [theatersMap, setTheatersMap] = useState<Map<string, string>>(new Map())
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null)
  const [userCategories, setUserCategories] = useState<Record<string, string>>({})
  const [selectedTheater, setSelectedTheater] = useState<string | null>(null)
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null)
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null)
  const [scheduledShowtimesByTheater, setScheduledShowtimesByTheater] = useState<Map<string, any[]>>(new Map())
  const [loadingScheduledShowtimes, setLoadingScheduledShowtimes] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'forgotPassword'>('login')
  const [authEmail, setAuthEmail] = useState('')
  const [authPassword, setAuthPassword] = useState('')
  const [authConfirmPassword, setAuthConfirmPassword] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)
  const [authSuccessMessage, setAuthSuccessMessage] = useState<string | null>(null)
  const [isAuthLoading, setIsAuthLoading] = useState(false)
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false)
  const supabase = createClient()

  useEffect(() => {
    const initialize = async () => {
      // Check auth but don't require it
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (user) {
        setUser(user)
        await loadUserCategories(user.id)
      }
      
      // Load movies regardless of auth status
      await loadMovies()
    }

    initialize()
  }, [])

  const loadUserCategories = async (userId: string) => {
    const { data, error } = await supabase
      .from('user_movie_categories')
      .select('movie_id, category')
      .eq('user_id', userId)

    if (!error && data) {
      const categories = data.reduce(
        (acc, item) => {
          let uiCategory = item.category
          if (item.category === 'to_watch') uiCategory = 'starred'
          if (item.category === 'watch_again') uiCategory = 'watch-again'
          return {
            ...acc,
            [item.movie_id]: uiCategory,
          }
        },
        {}
      )
      setUserCategories(categories)
    }
  }

  const loadScheduledShowtimes = async (theaterIds: string[]) => {
    setLoadingScheduledShowtimes(true)
    try {
      const idsParam = theaterIds.join(',')
      const res = await fetch(`/api/showtimes/scheduled?theaterIds=${idsParam}`)
      const data = await res.json()
      if (data.scheduledShowtimes) {
        const scheduledShowtimesMap = new Map<string, any[]>()
        Object.entries(data.scheduledShowtimes).forEach(([theaterId, showtimes]) => {
          scheduledShowtimesMap.set(theaterId, showtimes as any[])
        })
        setScheduledShowtimesByTheater(scheduledShowtimesMap)
      }
      if (data.movies && data.movies.length > 0) {
        setAllMovies((prev) => {
          const existingIds = new Set(prev.map((m) => m.movieId))
          
          const today = new Date()
          today.setHours(0, 0, 0, 0)

          const newMovies: Movie[] = data.movies
            .filter((m: any) => !existingIds.has(String(m.id)))
            .map((movieData: any) => {
              const releaseDate = movieData.releaseDateUtc ? new Date(movieData.releaseDateUtc) : null
              const isUpcoming = releaseDate ? releaseDate > today : true

              return {
                movieId: String(movieData.id),
                movieName: movieData.name,
                genre: movieData.genre,
                mpaaRating: movieData.rating,
                runTime: movieData.runtime,
                posterUrl: movieData.poster || movieData.hero,
                availableForAList: movieData.availableForAList,
                releaseDateUtc: movieData.releaseDateUtc,
                showtimes: [],
                isUpcoming,
              }
            })

          if (newMovies.length > 0) {
            return [...prev, ...newMovies]
          }
          return prev
        })
      }
    } catch (err) {
      console.error('[HomePage] Error loading scheduled showtimes:', err)
    } finally {
      setLoadingScheduledShowtimes(false)
    }
  }

  const loadMovies = async (locationOverride?: UserLocation) => {
    setLoading(true)
    try {
      // Get user location (use override if provided, otherwise fetch)
      const location = locationOverride || await getLocation()
      
      // Store the location for UI display
      setUserLocation({
        latitude: location.latitude,
        longitude: location.longitude,
        label: location.label,
        source: location.source,
      })

      // Format today's date for API
      const today = new Date()
      const todayStr = formatDateForAPI(today)

      // Fetch showtimes for today (API key is handled server-side)
      const response = await fetch(
        `/api/showtimes?date=${todayStr}&latitude=${location.latitude}&longitude=${location.longitude}`
      )
      const data = await response.json()

      if (data.movies && data.showtimes && data.theaters) {
        // Create theaters map for lookup (id -> name)
        const thMap = new Map<string, string>(
          data.theaters.map((t: any) => [String(t.id), t.name])
        )
        setTheatersMap(thMap)

        // Use theater data from API response (already includes coordinates and distances)
        const theaterObjects: Theatre[] = data.theaters
          .map((t: any) => ({
            theatreId: String(t.id),
            theatreName: t.name,
            address: t.address,
            city: t.city,
            state: t.state,
            postalCode: t.postalCode,
            latitude: t.latitude,
            longitude: t.longitude,
            distance: t.distance,
          }))
          .sort((a, b) => (a.distance ?? Number.MAX_VALUE) - (b.distance ?? Number.MAX_VALUE))

        const nearestTheaterObjects = theaterObjects.slice(0, 5)
        const nearestTheaterIds = nearestTheaterObjects.map((t) => t.theatreId)

        setTheaters(nearestTheaterObjects)

        // Reset scheduled showtimes first
        setScheduledShowtimesByTheater(new Map())

        // Fetch scheduled showtimes for the top 5 nearest theaters in the background
        if (nearestTheaterIds.length > 0) {
          loadScheduledShowtimes(nearestTheaterIds)
        }

        // Remove showtimes that are not in the top 5 nearest theaters
        const nearestTheaterIdsSet = new Set(nearestTheaterIds.map(id => String(id)))
        const showtimesForNearestTheaters = data.showtimes.filter((showtime: any) =>
          nearestTheaterIdsSet.has(String(showtime.theatreId))
        )

        // Extract dates, formats, and languages from showtimes
        const datesSet = new Set<string>()
        const formatsSet = new Set<string>()
        const languagesSet = new Set<string>()

        // Group showtimes by movieId
        const showtimesByMovie = new Map<string, Showtime[]>()
        showtimesForNearestTheaters.forEach((showtime: any) => {
          const movieId = showtime.movieId
          if (!showtimesByMovie.has(movieId)) {
            showtimesByMovie.set(movieId, [])
          }

          // Parse format and language from attributes
          const parsed = parseShowtimeAttributes(showtime.attributes)
          if (parsed.format) {
            formatsSet.add(parsed.format)
          }
          if (parsed.language) {
            languagesSet.add(parsed.language)
          }

          // Extract date
          const dateOnly = showtime.showDateTimeLocal?.split('T')[0]
          if (dateOnly) {
            datesSet.add(dateOnly)
          }

          const showtimeObj: Showtime = {
            showId: String(showtime.showId || showtime.id || ''),
            theatreId: String(showtime.theatreId),
            theatreName: thMap.get(String(showtime.theatreId)) || 'Unknown Theater',
            movieId: String(movieId),
            movieName: showtime.movieName,
            showDateTimeLocal: showtime.showDateTimeLocal,
            showDateTimeUtc: showtime.showDateTimeUtc,
            sellUntilDateTimeUtc: showtime.sellUntilDateTimeUtc,
            isSoldOut: showtime.isSoldOut,
            isCanceled: showtime.isCanceled,
            purchaseUrl: showtime.purchaseUrl,
            attributes: showtime.attributes || [],
            posterUrl: showtime.media?.posterDynamic,
          }
          showtimesByMovie.get(movieId)!.push(showtimeObj)
        })

        // Build movies list from backend data and attach showtimes
        const moviesList: Movie[] = data.movies
          .map((movieData: any) => ({
            movieId: String(movieData.id),
            movieName: movieData.name,
            genre: movieData.genre,
            mpaaRating: movieData.rating,
            runTime: movieData.runtime,
            posterUrl: movieData.poster,
            availableForAList: movieData.availableForAList,
            releaseDateUtc: movieData.releaseDateUtc,
            showtimes: showtimesByMovie.get(movieData.id) || [],
            isUpcoming: false,
          } as Movie))
          // Keep all movies from today's API — they are Now Playing even if all
          // individual shows have already started (AMC omits past showtimes).
          // The scheduled feed will merge future/today showtimes in below.

        setAllMovies(moviesList)
        setMovies(moviesList)
      } else {
        console.error('Failed to load movies:', data.error)
      }
    } catch (error) {
      console.error('Error loading movies:', error)
    } finally {
      setLoading(false)
    }
  }



  // Dynamic merge of today's showtimes and future scheduled showtimes for all movies
  const allMoviesWithShowtimes = useMemo(() => {
    return allMovies.map(movie => {
      const merged = [...movie.showtimes]
      const existingShowIds = new Set(movie.showtimes.map(s => String(s.showId)))

      scheduledShowtimesByTheater.forEach((theatreShowtimes) => {
        theatreShowtimes.forEach((scheduled: any) => {
          if (String(scheduled.movieId) === String(movie.movieId)) {
            const showId = String(scheduled.showId || scheduled.id)
            if (!existingShowIds.has(showId)) {
              const dateTimeLocal = scheduled.showDateTimeLocal || scheduled.showDateTimeUtc || ''
              if (dateTimeLocal) {
                merged.push({
                  showId: showId,
                  movieId: String(scheduled.movieId),
                  theatreId: String(scheduled.theatreId),
                  theatreName: theatersMap.get(String(scheduled.theatreId)) || 'Unknown Theater',
                  showDateTimeUtc: scheduled.showDateTimeUtc,
                  showDateTimeLocal: dateTimeLocal,
                  sellUntilDateTimeUtc: scheduled.sellUntilDateTimeUtc,
                  attributes: scheduled.attributes || [],
                  purchaseUrl: scheduled.purchaseUrl || scheduled.ticketingUrl || '',
                  movieName: scheduled.movieName || movie.movieName,
                  isSoldOut: scheduled.isSoldOut || false,
                  isCanceled: scheduled.isCanceled || false,
                })
                existingShowIds.add(showId)
              }
            }
          }
        })
      })

      return {
        ...movie,
        showtimes: merged,
      }
    })
  }, [allMovies, scheduledShowtimesByTheater, theatersMap])


  // Derive filter options dynamically from all currently loaded showtimes
  const filterOptions = useMemo<FilterOptions>(() => {
    const formatsSet = new Set<string>()
    const languagesSet = new Set<string>()
    const datesSet = new Set<string>()

    allMoviesWithShowtimes.forEach(movie => {
      movie.showtimes.forEach(s => {
        const parsed = parseShowtimeAttributes(s.attributes)
        if (parsed.format) {
          formatsSet.add(parsed.format)
        }
        if (parsed.language) {
          languagesSet.add(parsed.language)
        }
        const dateOnly = s.showDateTimeLocal?.split('T')[0]
        if (dateOnly) {
          datesSet.add(dateOnly)
        }
      })
    })

    return {
      formats: Array.from(formatsSet).sort((a, b) => {
        const priorityA = getFormatPriority(a)
        const priorityB = getFormatPriority(b)
        if (priorityA !== priorityB) {
          return priorityA - priorityB
        }
        return a.localeCompare(b)
      }),
      theaters: theaters,
      dates: Array.from(datesSet).sort(),
      times: [],
      languages: Array.from(languagesSet).sort(),
    }
  }, [allMoviesWithShowtimes, theaters])

  // Count active filters for badge
  const activeFilterCount = [selectedTheater, selectedFormat, selectedLanguage, selectedDate].filter(Boolean).length

  const clearAllFilters = () => {
    setSelectedTheater(null)
    setSelectedFormat(null)
    setSelectedDate(null)
    setSelectedLanguage(null)
    setSearchQuery('')
  }

  // Apply filters to movies
  useEffect(() => {
    if (allMoviesWithShowtimes.length === 0) {
      setMovies([])
      return
    }

    const filtered = allMoviesWithShowtimes.filter(movie => {
      // Hide movies with 0 total showtimes available
      if (!movie.showtimes || movie.showtimes.length === 0) {
        return false
      }

      // Apply search query filter if present
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim()
        const matchesName = movie.movieName.toLowerCase().includes(query)
        const matchesGenre = movie.genre ? movie.genre.toLowerCase().includes(query) : false
        if (!matchesName && !matchesGenre) {
          return false
        }
      }

      // If a theater is selected, check if movie has showtimes for that theater
      if (selectedTheater) {
        if (!movie.showtimes.some(s => s.theatreId === selectedTheater)) {
          return false
        }
      }

      // If a format is selected, check if movie has showtimes with that format
      if (selectedFormat) {
        if (
          !movie.showtimes.some(s => {
            const parsed = parseShowtimeAttributes(s.attributes)
            return parsed.format === selectedFormat
          })
        ) {
          return false
        }
      }

      // If a language is selected, check if movie has showtimes with that language
      if (selectedLanguage) {
        if (
          !movie.showtimes.some(s => {
            const parsed = parseShowtimeAttributes(s.attributes)
            return parsed.language === selectedLanguage
          })
        ) {
          return false
        }
      }

      // If a date is selected, check if movie has showtimes on that date
      if (selectedDate) {
        if (
          !movie.showtimes.some(s => {
            const dateOnly = s.showDateTimeLocal?.split('T')[0]
            return dateOnly === selectedDate
          })
        ) {
          return false
        }
      }

      return true
    })

    setMovies(filtered)
  }, [
    selectedTheater,
    selectedFormat,
    selectedLanguage,
    selectedDate,
    searchQuery,
    allMoviesWithShowtimes,
  ])

  const handleCategoryChange = async (movieId: string, category: string) => {
    if (!user) return

    let dbCategory = category
    if (category === 'starred') dbCategory = 'to_watch'
    if (category === 'watch-again') dbCategory = 'watch_again'

    const { error } = await supabase.from('user_movie_categories').upsert(
      {
        user_id: user.id,
        movie_id: movieId,
        category: dbCategory,
      },
      { onConflict: 'user_id,movie_id' }
    )

    if (!error) {
      setUserCategories(prev => ({
        ...prev,
        [movieId]: category,
      }))

      if (selectedMovie?.movieId === movieId) {
        setSelectedMovie(prev =>
          prev ? { ...prev } : null
        )
      }
    } else {
      console.error('[Category] Error updating category:', error)
    }
  }

  const handleLocationChange = (newLocation: StoredLocation) => {
    const location: UserLocation = {
      latitude: newLocation.latitude,
      longitude: newLocation.longitude,
      label: newLocation.label,
      source: newLocation.source,
    }
    // Reload movies with new location
    loadMovies(location)
  }

  const openAuthModal = (mode: 'login' | 'signup' | 'forgotPassword' = 'login') => {
    setAuthMode(mode)
    setAuthError(null)
    setAuthSuccessMessage(null)
    setIsAuthLoading(false)
    setAuthEmail('')
    setAuthPassword('')
    setAuthConfirmPassword('')
    setShowFilters(false)
    setIsAuthDialogOpen(true)
  }

  const closeAuthModal = () => {
    setAuthError(null)
    setAuthSuccessMessage(null)
    setIsAuthLoading(false)
    setIsAuthDialogOpen(false)
  }

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setAuthError(null)
    setAuthSuccessMessage(null)
    setIsAuthLoading(true)

    try {
      const {
        data: { user },
        error,
      } = await supabase.auth.signInWithPassword({
        email: authEmail,
        password: authPassword,
      })

      if (error) throw error
      if (!user) throw new Error('Unable to sign in. Please try again.')

      setUser(user)
      await loadUserCategories(user.id)
      closeAuthModal()
    } catch (error: unknown) {
      setAuthError(error instanceof Error ? error.message : 'An error occurred while signing in.')
    } finally {
      setIsAuthLoading(false)
    }
  }

  const handleSignUp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setAuthError(null)
    setAuthSuccessMessage(null)

    if (authPassword !== authConfirmPassword) {
      setAuthError('Passwords do not match')
      return
    }

    if (authPassword.length < 6) {
      setAuthError('Password must be at least 6 characters')
      return
    }

    setIsAuthLoading(true)

    try {
      const { error } = await supabase.auth.signUp({
        email: authEmail,
        password: authPassword,
        options: {
          emailRedirectTo:
            process.env.NEXT_PUBLIC_DEV_SUPABASE_REDIRECT_URL ??
            `${window.location.origin}/auth/callback`,
        },
      })

      if (error) throw error
      setAuthSuccessMessage(`We sent a confirmation link to ${authEmail}. Check your email to activate your account.`)
    } catch (error: unknown) {
      setAuthError(error instanceof Error ? error.message : 'An error occurred while creating your account.')
    } finally {
      setIsAuthLoading(false)
    }
  }

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setAuthError(null)
    setAuthSuccessMessage(null)
    setIsAuthLoading(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail({
        email: authEmail,
        options: {
          emailRedirectTo:
            process.env.NEXT_PUBLIC_DEV_SUPABASE_REDIRECT_URL ??
            `${window.location.origin}/auth/callback`,
        },
      })

      if (error) throw error
      setAuthSuccessMessage(`If that email exists, we sent a password reset link to ${authEmail}.`) 
    } catch (error: unknown) {
      setAuthError(error instanceof Error ? error.message : 'An error occurred while sending password reset instructions.')
    } finally {
      setIsAuthLoading(false)
    }
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setUserCategories({})
  }

  // Show loading spinner while initializing
  if (loading && movies.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin" size={32} />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-2">
          {mobileSearchOpen ? (
            <div className="flex items-center gap-2 w-full sm:hidden">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search movies or genres..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition h-[38px] bg-slate-50 text-slate-900 placeholder:text-slate-400"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setMobileSearchOpen(false)
                  setSearchQuery('')
                }}
                className="text-sm font-medium text-slate-600 px-2 py-1 hover:text-slate-900"
              >
                Cancel
              </button>
            </div>
          ) : (
            <>
              <div className="min-w-0 flex-1 sm:flex-initial">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight">AMC Showtimes</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <LocationPicker
                    currentLocation={userLocation || undefined}
                    onLocationChange={handleLocationChange}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Search input for desktop */}
                <div className="relative hidden sm:block w-48 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search movies or genres..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition h-[38px] text-slate-900 placeholder:text-slate-400"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Mobile search icon button */}
                <button
                  type="button"
                  onClick={() => setMobileSearchOpen(true)}
                  className="sm:hidden flex items-center justify-center w-[38px] h-[38px] rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
                  aria-label="Open search"
                >
                  <Search size={16} />
                </button>

                {/* Filter button with active count badge */}
                <button
                  type="button"
                  onClick={() => setShowFilters(true)}
                  className="relative inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition h-[38px]"
                  aria-label="Open filters"
                >
                  <SlidersHorizontal size={16} />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 rounded-full bg-slate-900 text-white text-[10px] font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                {user ? (
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition"
                  >
                    Logout
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => openAuthModal('login')}
                    className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      <Dialog open={isAuthDialogOpen} onOpenChange={(open) => { if (!open) closeAuthModal(); setIsAuthDialogOpen(open) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {authMode === 'login' && 'Sign in'}
              {authMode === 'signup' && 'Create account'}
              {authMode === 'forgotPassword' && 'Forgot password'}
            </DialogTitle>
            <DialogDescription>
              {authMode === 'login' && 'Sign in to see your personalized categories without reloading showtimes.'}
              {authMode === 'signup' && 'Create an account to save your movie preferences.'}
              {authMode === 'forgotPassword' && 'Enter your email to receive password reset instructions.'}
            </DialogDescription>
          </DialogHeader>
          {authSuccessMessage ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">{authSuccessMessage}</p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAuthMode('login')}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition"
                >
                  Back to sign in
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={authMode === 'login' ? handleLogin : authMode === 'signup' ? handleSignUp : handleForgotPassword}
              className="space-y-4"
            >
              <div>
                <label htmlFor="auth-email" className="block text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  id="auth-email"
                  type="email"
                  value={authEmail}
                  onChange={(event) => setAuthEmail(event.target.value)}
                  required
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-900 focus:ring-slate-900"
                />
              </div>

              {(authMode === 'login' || authMode === 'signup') && (
                <div>
                  <label htmlFor="auth-password" className="block text-sm font-medium text-slate-700">
                    Password
                  </label>
                  <input
                    id="auth-password"
                    type="password"
                    value={authPassword}
                    onChange={(event) => setAuthPassword(event.target.value)}
                    required
                    className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-900 focus:ring-slate-900"
                  />
                </div>
              )}

              {authMode === 'signup' && (
                <div>
                  <label htmlFor="auth-confirm-password" className="block text-sm font-medium text-slate-700">
                    Confirm password
                  </label>
                  <input
                    id="auth-confirm-password"
                    type="password"
                    value={authConfirmPassword}
                    onChange={(event) => setAuthConfirmPassword(event.target.value)}
                    required
                    className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-900 focus:ring-slate-900"
                  />
                </div>
              )}

              {authError && (
                <p className="text-sm text-red-600">{authError}</p>
              )}

              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeAuthModal}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAuthLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isAuthLoading
                    ? authMode === 'forgotPassword'
                      ? 'Sending…'
                      : 'Submitting…'
                    : authMode === 'login'
                    ? 'Sign In'
                    : authMode === 'signup'
                    ? 'Sign Up'
                    : 'Send reset link'}
                </button>
              </div>

              <div className="flex flex-col gap-2 pt-2 text-sm text-slate-600">
                {authMode === 'login' && (
                  <>
                    <button
                      type="button"
                      onClick={() => setAuthMode('forgotPassword')}
                      className="text-slate-900 underline underline-offset-2"
                    >
                      Forgot password?
                    </button>
                    <button
                      type="button"
                      onClick={() => setAuthMode('signup')}
                      className="text-slate-900 underline underline-offset-2"
                    >
                      Create an account
                    </button>
                  </>
                )}
                {authMode === 'signup' && (
                  <button
                    type="button"
                    onClick={() => setAuthMode('login')}
                    className="text-slate-900 underline underline-offset-2"
                  >
                    Already have an account? Sign in
                  </button>
                )}
                {authMode === 'forgotPassword' && (
                  <button
                    type="button"
                    onClick={() => setAuthMode('login')}
                    className="text-slate-900 underline underline-offset-2"
                  >
                    Back to sign in
                  </button>
                )}
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Filter Modal */}
      <Dialog open={showFilters} onOpenChange={setShowFilters}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Filters</DialogTitle>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 transition"
                >
                  Clear all ({activeFilterCount})
                </button>
              )}
            </div>
            <DialogDescription>
              Narrow down movies by theater, format, language, or date.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            {/* Theater Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Theater</label>
              <select
                value={selectedTheater || ''}
                onChange={e => setSelectedTheater(e.target.value || null)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
              >
                <option value="">All Theaters</option>
                {filterOptions.theaters.map(theater => (
                  <option key={theater.theatreId} value={theater.theatreId}>
                    {theater.theatreName}
                  </option>
                ))}
              </select>
            </div>

            {/* Format Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Format</label>
              <select
                value={selectedFormat || ''}
                onChange={e => setSelectedFormat(e.target.value || null)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
              >
                <option value="">All Formats</option>
                {filterOptions.formats.map(format => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Date</label>
              <select
                value={selectedDate || ''}
                onChange={e => setSelectedDate(e.target.value || null)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
              >
                <option value="">All Dates</option>
                {filterOptions.dates.map(date => (
                  <option key={date} value={date}>
                    {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </option>
                ))}
              </select>
            </div>

            {/* Language Filter */}
            {filterOptions.languages && filterOptions.languages.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Language</label>
                <select
                  value={selectedLanguage || ''}
                  onChange={e => setSelectedLanguage(e.target.value || null)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
                >
                  <option value="">All Languages</option>
                  {filterOptions.languages.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Active filter chips */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                {selectedTheater && (
                  <div className="flex items-center gap-1.5 bg-slate-900 text-white text-xs px-2.5 py-1 rounded-full">
                    <span>{filterOptions.theaters.find(t => t.theatreId === selectedTheater)?.theatreName}</span>
                    <button onClick={() => setSelectedTheater(null)} className="hover:text-slate-300"><X size={12} /></button>
                  </div>
                )}
                {selectedFormat && (
                  <div className="flex items-center gap-1.5 bg-slate-900 text-white text-xs px-2.5 py-1 rounded-full">
                    <span>{selectedFormat}</span>
                    <button onClick={() => setSelectedFormat(null)} className="hover:text-slate-300"><X size={12} /></button>
                  </div>
                )}
                {selectedLanguage && (
                  <div className="flex items-center gap-1.5 bg-slate-900 text-white text-xs px-2.5 py-1 rounded-full">
                    <span>{selectedLanguage}</span>
                    <button onClick={() => setSelectedLanguage(null)} className="hover:text-slate-300"><X size={12} /></button>
                  </div>
                )}
                {selectedDate && (
                  <div className="flex items-center gap-1.5 bg-slate-900 text-white text-xs px-2.5 py-1 rounded-full">
                    <span>{new Date(selectedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    <button onClick={() => setSelectedDate(null)} className="hover:text-slate-300"><X size={12} /></button>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setShowFilters(false)}
              className="w-full mt-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto sm:px-6 lg:px-8 py-0 sm:py-8">

        {/* Movies Display */}
        {/* Movies Display */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin mr-2" size={24} />
            <span>Loading movies...</span>
          </div>
        ) : movies.length > 0 ? (
          <>
            <MovieGrid
              movies={movies}
              onMovieClick={setSelectedMovie}
              userCategories={userCategories}
              isFutureLoading={loadingScheduledShowtimes}
              hasActiveFilters={activeFilterCount > 0 || Boolean(searchQuery.trim())}
            />
            {/* Movie Detail Dialog */}
            <Dialog open={selectedMovie !== null} onOpenChange={() => setSelectedMovie(null)}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <ErrorBoundary componentName="MovieDetail">
                  {selectedMovie && (
                    <MovieDetail
                      movie={selectedMovie}
                      onBack={() => setSelectedMovie(null)}
                      isAuthenticated={!!user}
                      userCategory={userCategories[selectedMovie.movieId]}
                      onCategoryChange={handleCategoryChange}
                      onRequestLogin={() => openAuthModal('login')}
                      theaterNames={theatersMap}
                      theaters={theaters}
                      userLocation={userLocation}
                      scheduledShowtimesByTheater={scheduledShowtimesByTheater}
                      isLoadingScheduledShowtimes={loadingScheduledShowtimes}
                      initialDate={selectedDate}
                      initialFormat={selectedFormat}
                      initialLanguage={selectedLanguage}
                      initialTheater={selectedTheater}
                    />
                  )}
                </ErrorBoundary>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <div className="text-center py-20 px-4">
            <p className="text-slate-600 text-lg">
              {allMovies.length > 0
                ? 'No movies match your filters. Try adjusting your selection.'
                : 'No movies found. Please try again later.'}
            </p>
            <button
              onClick={allMovies.length > 0 ? clearAllFilters : () => window.location.reload()}
              className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition"
            >
              {allMovies.length > 0 ? 'Clear Filters' : 'Refresh'}
            </button>
          </div>
        )}
      </main>
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-12 border-t border-slate-200 text-center text-xs text-slate-400">
        <p>
          &copy; {new Date().getFullYear()} AMC Showtimes.{' '}
          <a
            href="https://www.flaticon.com/free-icons/tv-watching"
            title="tv watching icons"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-slate-600 underline transition"
          >
            Tv watching icons created by iconixar - Flaticon
          </a>
        </p>
      </footer>
    </div>
  )
}
